import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, Filter, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CongressFilter from '../filters/CongressFilter';
import DateFilter from '../filters/DateFilter';
import CommitteeFilter from '../filters/CommitteeFilter';
import StageFilter from '../filters/StageFilter';
import { useBillContext } from '../../context/BillContext';
import { Committee, LegislativeStatus } from '../../types/types';
import { Bill } from '../../types/types';
import { supabase } from '../../lib/supabaseClient';

const SearchHeader: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const searchRef = useRef<HTMLDivElement>(null);
  const { bills } = useBillContext();
  const [suggestions, setSuggestions] = useState<Bill[]>([]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowFilters(false);
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Extract available committees from bills
  const availableCommittees = React.useMemo(() => {
    const committeesSet = new Set();
    const committees: Committee[] = [];
    
    bills.forEach(bill => {
      bill.committees?.forEach(activity => {
        const key = `${activity.committee.name}-${activity.committee.chamber}`;
        if (!committeesSet.has(key)) {
          committeesSet.add(key);
          committees.push(activity.committee);
        }
      });
    });
    
    return committees.sort((a, b) => a.name.localeCompare(b.name));
  }, [bills]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) { setSuggestions([]); return; }
    const timer = window.setTimeout(async () => {
      const safe = trimmed.replace(/[^a-zA-Z0-9 .-]/g, '');
      const compact = safe.replace(/[^a-zA-Z0-9]/g, '');
      let request = supabase.from('bills')
        .select('id,number,title,bill_type,bill_number,bill_sponsors!inner(id)')
        .eq('congress', '119')
        .not('original_text', 'is', null)
        .limit(5);
      request = /^[a-zA-Z]+\d+$/.test(compact)
        ? request.ilike('number', `%${compact}%`)
        : request.or(`number.ilike.%${safe}%,title.ilike.%${safe}%`);
      const { data } = await request;
      setSuggestions((data || []).map(item => ({ ...item, billType:item.bill_type, billNumber:item.bill_number })) as Bill[]);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [query]);

  // Calculate bill counts by stage
  const billCountsByStage = React.useMemo(() => {
    const counts = {} as Record<LegislativeStatus, number>;
    
    bills.forEach(bill => {
      counts[bill.status] = (counts[bill.status] || 0) + 1;
    });
    
    return counts;
  }, [bills]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (query) {
      params.set('q', query);
    } else {
      params.delete('q');
    }
    navigate(`/search?${params.toString()}`);
    setIsExpanded(false);
    setShowFilters(false);
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    const params = new URLSearchParams(searchParams);
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(','));
      } else {
        params.delete(key);
      }
    } else {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    }
    navigate(`/search?${params.toString()}`);
  };

  const getSelectedCommittees = (): string[] => {
    const committees = searchParams.get('committees');
    return committees ? committees.split(',') : [];
  };

  const getSelectedStages = (): LegislativeStatus[] => {
    const stages = searchParams.get('stages');
    return stages ? stages.split(',') as LegislativeStatus[] : [];
  };

  return (
    <div ref={searchRef} className="relative flex-1 max-w-3xl mx-4">
      <form onSubmit={handleSearch} className="relative">
        <div className="relative">
          <Search 
            size={18} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-400"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsExpanded(true)}
            placeholder="Search current, information-rich bills..."
            className="w-full bg-gray-100 border border-gray-300 rounded-full py-2 pl-10 pr-12 text-sm placeholder-gray-500 focus:outline-none focus:bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:bg-gray-800 dark:focus:border-primary-700"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors duration-200 dark:hover:bg-gray-700"
              >
                <X size={14} className="text-gray-500 dark:text-gray-400" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`p-1 hover:bg-gray-200 rounded-full transition-colors duration-200 ${showFilters ? 'bg-gray-200 dark:bg-gray-700' : ''} dark:hover:bg-gray-700`}
            >
              <Filter size={14} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </form>

      <AnimatePresence>
        {isExpanded && query.trim().length >= 2 && !showFilters && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute top-full left-0 right-0 mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl z-50 dark:bg-gray-800 dark:border-gray-700"
          >
            {suggestions.length > 0 ? (
              <>
                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 dark:bg-gray-900 dark:text-gray-400">Information-rich bills from the 119th Congress</div>
                {suggestions.map(bill => (
                  <button
                    key={bill.id}
                    type="button"
                    onClick={() => {
                      navigate(`/bill/${bill.id}`);
                      setIsExpanded(false);
                    }}
                    className="flex w-full items-center justify-between gap-4 border-t border-gray-100 px-4 py-3 text-left hover:bg-primary-50 dark:border-gray-700 dark:hover:bg-gray-700"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-primary-700 dark:text-primary-300">{bill.number}</span>
                      <span className="block truncate text-sm text-gray-700 dark:text-gray-200">{bill.title}</span>
                    </span>
                    <ArrowRight size={17} className="shrink-0 text-gray-400" />
                  </button>
                ))}
                <button type="button" onClick={() => navigate(`/search?q=${encodeURIComponent(query.trim())}`)} className="flex w-full items-center justify-center gap-2 border-t border-gray-200 px-4 py-3 text-sm font-medium text-primary-700 hover:bg-gray-50 dark:border-gray-700 dark:text-primary-300 dark:hover:bg-gray-700">
                  See all results for “{query}” <ArrowRight size={16} />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/search?q=${encodeURIComponent(query.trim())}`)}
                className="flex w-full items-center justify-between px-4 py-4 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Search all bills for “{query}” <ArrowRight size={16} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 dark:bg-gray-800 dark:border-gray-700"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <CongressFilter
                selectedCongress={searchParams.get('congress')}
                onCongressChange={(value) => handleFilterChange('congress', value)}
              />
              
              <DateFilter
                selectedDate={searchParams.get('introduced_date')}
                onDateChange={(value) => handleFilterChange('introduced_date', value)}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Status
                </label>
                <select
                  value={searchParams.get('status') || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Statuses</option>
                  <option value="introduced" className="dark:bg-gray-700 dark:text-white">Introduced</option>
                  <option value="referred_to_committee" className="dark:bg-gray-700 dark:text-white">In Committee</option>
                  <option value="reported_by_committee" className="dark:bg-gray-700 dark:text-white">Reported</option>
                  <option value="passed_house" className="dark:bg-gray-700 dark:text-white">Passed House</option>
                  <option value="passed_senate" className="dark:bg-gray-700 dark:text-white">Passed Senate</option>
                  <option value="to_president" className="dark:bg-gray-700 dark:text-white">To President</option>
                  <option value="enacted" className="dark:bg-gray-700 dark:text-white">Enacted</option>
                  <option value="vetoed" className="dark:bg-gray-700 dark:text-white">Vetoed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Chamber
                </label>
                <select
                  value={searchParams.get('chamber') || ''}
                  onChange={(e) => handleFilterChange('chamber', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Chambers</option>
                  <option value="house" className="dark:bg-gray-700 dark:text-white">House</option>
                  <option value="senate" className="dark:bg-gray-700 dark:text-white">Senate</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Category
                </label>
                <select
                  value={searchParams.get('category') || ''}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Categories</option>
                  <option value="recent" className="dark:bg-gray-700 dark:text-white">Recent</option>
                  <option value="upcoming" className="dark:bg-gray-700 dark:text-white">Upcoming</option>
                  <option value="enacted" className="dark:bg-gray-700 dark:text-white">Enacted</option>
                </select>
              </div>
            </div>

            {/* Advanced Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <CommitteeFilter
                selectedCommittees={getSelectedCommittees()}
                onCommitteeChange={(committees) => handleFilterChange('committees', committees)}
                availableCommittees={availableCommittees}
              />

              <StageFilter
                selectedStages={getSelectedStages()}
                onStageChange={(stages) => handleFilterChange('stages', stages)}
                billCounts={billCountsByStage}
              />
            </div>

            <div className="mt-4 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  navigate('/search');
                  setShowFilters(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none dark:text-gray-200 dark:hover:text-white"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Apply Filters
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchHeader;
